import React, { useEffect, useState, useRef } from 'react';
import { Layout } from 'antd';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import PropTypes from 'prop-types';

import { sanitizeObjectDeep } from 'utils/xss';
import helpersUtils from 'helpers';
import helpers, { scrollToSection } from 'helpers/proposal';
import Loader from 'components/Loader';
import ViralLoopItem from 'components/ViralLoopItem';
import PageBreakers from 'components/PageBreakers';

import DropArrowIcon from 'components/Icons/DropArrowIcon';
import useWindowDimensions from 'hooks/useWindowDimensions';
import AttachmentSection from 'pages/Proposal/components/Draft/components/AttachmentSection/AttachmentSection';
import ProposalHeaderBar from 'pages/Proposal/components/ProposalHeaderBar';
import ProposalWixHeaderBar from 'pages/Proposal/components/ProposalWixHeaderBar';
import ProposalMetaInformation from '../ProposalMetaInformation';
import PublishFooter from '../PublishFooter';
import ContentTable from '../../../ContentTable/ContentTable';
import { RTL_LANGUAGES } from 'constants/languages';

import {
  PublishHeaderSection,
  PublishSection,
  PublishSignatureSection,
  PublishApprovedInfo,
  AllVersionsSidebar,
} from './components';

import './PublishContent.scss';

const PublishContent = (props) => {
  const contentRef = useRef(null);
  const redirectTimerRef = useRef({});
  const {
    config,
    prop,
    user,
    setProp,
    updateProposalClient,
    previewTemplate,
    isPublishing,
    publishProposal,
    restoreProposal,
    copyPropsalLink,
    publishedProposalLink,
    setMakePayment,
    downloadPdf,
    downloadingPDF,
    handleDownloadCancel,
    isTemplate,
    isSection,
    isHistory,
    proposalSettings,
    isSavingForm,
    setIsSavingForm,
    wixPreview,
    addItem,
    setOpenWixPreview,
    clientWixPreview,
    templateWixPreview,
    userWixPreview,
    isEditingModal,
    templateUserWixPreview,
    isHeader,
    spamUrls,
    setProposalPublished,
    backupPreviewId,
    setBackupPreviewId,
    backupPreviewLoading,
    restoring,
    isWix,
    isThumbnail,
    path,
    pageTimerRef: propsPageTimerRef,
    currentPageRef: propsCurrentPageRef,
    pageStartTimeRef: propsPageStartTimeRef,
    fromActivityModal,
    heatmaps,
  } = props;
  const internalPageTimerRef = useRef({ 1: 0 });
  const internalCurrentPageRef = useRef(1);
  const internalPageStartTimeRef = useRef(null);
  const pageTimerRef = propsPageTimerRef ?? internalPageTimerRef;
  const currentPageRef = propsCurrentPageRef ?? internalCurrentPageRef;
  const pageStartTimeRef = propsPageStartTimeRef ?? internalPageStartTimeRef;
  const variables = user?.adminFeatureFlags?.find((flag) => flag.key === 'HTML_IN_VARIABLES')
    ?.enabled
    ? prop?.draft?.variables
    : sanitizeObjectDeep(prop?.draft?.variables);

  const { cid } = useParams();
  const { pathname } = useLocation();
  const history = useHistory();

  const { trackEvent } = useIntercom();

  const [showHeader, showOrHideHeader] = useState(true);
  const [showValidationError, setShowValidationError] = useState(false);
  const [sectionHeight, setSectionHeight] = useState(0);
  const [screen, setScreen] = useState('desktop');
  const [collapseContentTable, setCollapseContentTable] = useState(true);
  const [viewType, setViewType] = useState('');
  const [sectionOrderArrangement, setSectionOrderArrangement] = useState(
    prop?.draft?.sectionorder || []
  );
  const [downloadingFile, setDownloadingFile] = useState(false);

  /**
   * Track the current page based on the scroll position.
   * Uses an independent timestamp (pageStartTimeRef) so the global timer for `watched` is unaffected.
   */
  useEffect(() => {
    // bots never generate meaningful page time, and logging is skipped for them
    // anyway — don't let the refs accumulate on their behalf
    if (helpersUtils.isBotOrPrerender()) {
      return undefined;
    }

    const A4_PAGE_HEIGHT = 1250;

    // Page breaks are measured from the top of the content, but scrollY is measured
    // from the top of the document. `.publish-content` sits below a breakpoint
    // dependent padding (50px on /cd/ at >=992px, 0 below — see Publish.scss), so
    // without this offset every boundary fires that many pixels early and the tail
    // of each page is logged against the next one.
    let contentTop = 0;
    let measured = false;

    const measureContentTop = () => {
      if (contentRef.current) {
        contentTop = contentRef.current.getBoundingClientRect().top + window.scrollY;
        measured = true;
      }
    };

    const handleScroll = () => {
      // the content may not have rendered yet when this effect first ran (data
      // still loading), so keep trying until we get a real measurement
      if (!measured) {
        measureContentTop();
      }

      const scrollY =
        window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      const page = Math.floor(Math.max(0, scrollY - contentTop) / A4_PAGE_HEIGHT) + 1;

      if (currentPageRef.current !== page) {
        const prevPage = currentPageRef.current;
        const now = Date.now();
        if (pageStartTimeRef.current == null) pageStartTimeRef.current = now;
        const timeOnPrevPageSeconds = (now - pageStartTimeRef.current) / 1000;
        pageTimerRef.current = {
          ...pageTimerRef.current,
          [prevPage]: (pageTimerRef.current[prevPage] || 0) + timeOnPrevPageSeconds,
        };
        currentPageRef.current = page;
        pageStartTimeRef.current = now;
      }
    };

    const opts = { passive: true, capture: true };

    // re-measure on anything that can move the content: breakpoint changes, and
    // late-loading images/fonts that reflow whatever sits above it
    measureContentTop();
    window.addEventListener('resize', measureContentTop);
    window.addEventListener('load', measureContentTop);
    window.addEventListener('scroll', handleScroll, opts);

    return () => {
      window.removeEventListener('resize', measureContentTop);
      window.removeEventListener('load', measureContentTop);
      window.removeEventListener('scroll', handleScroll, opts);
    };
  }, [currentPageRef, pageTimerRef, pageStartTimeRef]);

  const { width } = useWindowDimensions();

  const ispdf = pathname.indexOf('/PDF/') !== -1 || previewTemplate;
  const isWixClient = clientWixPreview;
  const allowPreviewDownload = user?.profile?.allowPreviewDownload !== false;
  const templateWixEditor = !!path?.includes('/template-editor');

  useEffect(() => {
    if (path === '/pt/:tid' || path === '/pd/:id') {
      setViewType('editor-preview');
    } else if (path === '/cd/:cid') {
      setViewType('client-preview');
      document.documentElement.style.setProperty('--header-height', '0px');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  useEffect(() => {
    if (pathname.indexOf('s=true') === -1) {
      const eventtype = cid ? 'new-client-prop-view' : 'new-user-prop-preview';
      if (pathname.indexOf('/cd/') === -1 || clientWixPreview)
        window.ga('send', 'event', eventtype, eventtype, 'init', pathname);

      if (!prop?.wixProposalId) {
        trackEvent(eventtype, {
          pid: prop?.pid,
          id: prop?._id,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  const showValidationErrorFunction = (ref) => {
    setShowValidationError(true);
    const refIndex = prop.draft.sectionorder.indexOf(ref);
    const section = document.getElementsByTagName('nobreak')?.[refIndex];
    if (section) {
      section.getElementsByClassName('form-error-text')[0]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }
  };

  const onRedirect = (redirectUrl, redirectAfter) => {
    const redirectFn = () => {
      window.location = redirectUrl;
      redirectTimerRef.current = {};
    };
    redirectTimerRef.current = {
      timer: setTimeout(redirectFn, redirectAfter ? redirectAfter * 1000 : 1000),
      redirectFn,
    };
  };

  const onDownloadPDF = () => {
    const { timer, redirectFn } = redirectTimerRef.current;
    clearTimeout(timer);
    downloadPdf({
      onDownloadCompleted: () => setTimeout(() => redirectFn?.(), 4000),
      onErrorPopupClose: redirectFn,
    });
  };

  const configText = (x, disableTrim, vars = {}) => {
    return helpers.configText({
      x,
      notmarked: true,
      config,
      prop,
      user: user,
      vars,
      disableTrim,
    });
  };

  useEffect(() => {
    if (contentRef?.current) {
      let height;
      if (fromActivityModal) {
        // The ancestor <section> has zoom: 0.66 (ActivityModal.scss), which scales
        // getBoundingClientRect into viewport coordinates. Dividing by 0.66 recovers
        // the true CSS pixel height needed to compute the correct numberOfBreaks in PageBreakers.
        const bcrHeight = contentRef.current.getBoundingClientRect().height;
        height = bcrHeight > 0 ? Math.round(bcrHeight / 0.66) : 0;
      } else {
        height = contentRef.current.clientHeight;
      }
      if (height) {
        handleHeightChange({ height });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentRef?.current, prop, fromActivityModal]);

  useEffect(() => {
    if (isHistory && prop?.state === 'approved' && prop?._id) {
      document.body.classList.remove('history-preview');
      history.push(`/pd/${prop._id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prop]);

  if (!prop) {
    return <Loader />;
  }

  const handleHeightChange = ({ height }) => {
    if (height !== sectionHeight) {
      setSectionHeight(height);
    }
  };

  const updateScreen = (screenCalib) => {
    setScreen(screenCalib);
  };

  const openPublishView = (openPreviewValue) => {
    setOpenWixPreview(openPreviewValue);
  };

  let publishSections = [];
  if (prop.draft) {
    const sections = [];
    const order =
      templateWixPreview || templateUserWixPreview
        ? prop.draft.sectionorder.filter((section) => section !== 'whyme2')
        : prop.draft.sectionorder || helpers.defaultSectionOrder;

    const proposalExclude = prop?.draft?.proposalExclude || [];

    order.forEach((sname, snameIndex) => {
      const s =
        prop.draft[sname] &&
        (prop.draft[sname].text || prop.draft[sname].raw) &&
        !proposalExclude.includes(sname) ? (
          <PublishSection
            key={sname}
            prop={prop}
            name={sname}
            configText={configText}
            ispdf={ispdf}
            updateProposalClient={updateProposalClient}
            showValidationError={showValidationError}
            location={props.location}
            setIsSavingForm={setIsSavingForm}
            proposalSettings={proposalSettings}
            screen={screen}
            wixPreview={wixPreview}
            clientWixPreview={clientWixPreview}
            previewTemplate={previewTemplate}
            templateWixPreview={templateWixPreview || templateUserWixPreview}
            userWixPreview={userWixPreview}
            variables={variables}
          />
        ) : null;
      if (s) {
        sections.push(s);
      }
    });
    if (order.length !== sectionOrderArrangement.length) {
      setSectionOrderArrangement(order);
    }

    publishSections = (
      <div
        id="section-content-container"
        className={`${screen === 'mobile' ? 'mobile-screen' : 'desktop-screen'} ${
          prop?.importedProposal ? 'imported-section-content-container' : ''
        }`}
        ref={contentRef}>
        {isSection && (isTemplate || isHeader || prop.project) && (
          <PublishHeaderSection
            name={isSection ? order : 'header'}
            proposal={prop}
            isPreview={false}
          />
        )}
        {!isSection && (
          <PublishHeaderSection
            name={'header'}
            proposal={prop}
            templateWixPreview={templateWixPreview || templateUserWixPreview}
          />
        )}
        {sections}
        {!isSection && (
          <PublishSignatureSection
            name="signature"
            proposal={prop}
            user={user}
            setProp={setProp}
            updateProposalClient={updateProposalClient}
            configText={configText}
            isClient={!!((cid || ispdf || isWixClient) && !user)}
            isSavingForm={isSavingForm}
            showValidationErrorFunction={showValidationErrorFunction}
            downloadPdf={onDownloadPDF}
            userWixPreview={userWixPreview}
            templateWixPreview={templateWixPreview || templateUserWixPreview}
            wixPreview={wixPreview}
            isEditingModal={isEditingModal}
            onRedirect={onRedirect}
            clientWixPreview={clientWixPreview}
            isHistory={isHistory}
            variables={variables}
          />
        )}
      </div>
    );
  }

  const toggleHeader = () => {
    showOrHideHeader((prev) => {
      const show = !prev;
      // update the header-height variable
      document.documentElement.style.setProperty('--header-height', `${show ? 64 : 0}px`);
      return show;
    });
  };

  const fontSizeRange = getSmallLargeFont(prop?.draft?.bodyFont?.fontSize);
  const showTocMenu =
    !isSection &&
    !ispdf &&
    !prop?.importedProposal &&
    !wixPreview &&
    !userWixPreview &&
    !templateWixEditor;
  const isRTL = RTL_LANGUAGES.includes(prop?.language?.toLowerCase());
  const gap =
    showTocMenu || allowPreviewDownload
      ? showTocMenu && allowPreviewDownload
        ? 'two'
        : 'one'
      : 'no-gap';

  const isWixModalPreview = window.self !== window.top && (templateWixPreview || clientWixPreview);

  return (
    <Layout
      className={`publish ${fromActivityModal ? 'activity-modal-publish' : ''} ${
        isWixModalPreview ? 'wix-preview-modal' : ''
      } ${isRTL ? 'display-rtl' : ''} ${showHeader ? '' : 'minimize'} ${fontSizeRange} ${
        pathname.includes('/cd/') || clientWixPreview ? 'notranslate' : ''
      }`}>
      {downloadingFile && <Loader overlay={true} />}
      {!previewTemplate && <ProposalMetaInformation proposal={prop} user={user} />}
      {!ispdf && !pathname.includes('/cd/') && !clientWixPreview && !userWixPreview && (
        <Layout.Header className="publish-header">
          {wixPreview || templateUserWixPreview ? (
            <ProposalWixHeaderBar
              mode={isHistory ? 'history' : 'publish'}
              user={user}
              proposal={prop}
              published={!!prop.published}
              copyPropsalLink={copyPropsalLink}
              publishedProposalLink={publishedProposalLink}
              openPublishView={openPublishView}
              isPublishing={isPublishing}
              publishProposal={publishProposal}
              downloadPdf={onDownloadPDF}
              isTemplate={isTemplate}
              isSection={isSection}
              updateScreen={updateScreen}
              screen={screen}
              collapseContentTable={collapseContentTable}
              setCollapseContentTable={setCollapseContentTable}
              addItem={addItem}
              templateUserWixPreview={templateUserWixPreview}
              restoreProposal={restoreProposal}
              backupPreviewId={backupPreviewId}
              setBackupPreviewId={setBackupPreviewId}
              backupPreviewLoading={backupPreviewLoading}
              restoring={restoring}
            />
          ) : (
            <>
              <ProposalHeaderBar
                mode={isHistory ? 'history' : 'publish'}
                user={user}
                proposal={prop}
                copyPropsalLink={copyPropsalLink}
                publishedProposalLink={publishedProposalLink}
                isPublishing={isPublishing}
                publishProposal={publishProposal}
                restoreProposal={restoreProposal}
                setProposalPublished={setProposalPublished}
                isTemplate={isTemplate}
                isSection={isSection}
                updateScreen={updateScreen}
                screen={screen}
                setCollapseContentTable={setCollapseContentTable}
                backupPreviewId={backupPreviewId}
                setBackupPreviewId={setBackupPreviewId}
                backupPreviewLoading={backupPreviewLoading}
                restoring={restoring}
              />
              {!isHistory && (
                <DropArrowIcon onClick={toggleHeader} className="publish-header-toggle-icon" />
              )}
            </>
          )}
        </Layout.Header>
      )}

      {!isSection && !ispdf && (
        <ContentTable
          configText={configText}
          collapse={collapseContentTable}
          setCollapse={setCollapseContentTable}
          proposal={prop}
          user={user}
          downloadPdf={onDownloadPDF}
          downloadingPDF={downloadingPDF}
          handleDownloadCancel={handleDownloadCancel}
          scrollToSection={scrollToSection}
          clientWixPreview={clientWixPreview}
          contentTableClassName={`publish-content-table${
            pathname.includes('/cd/') || clientWixPreview ? ' publish-content-table-preview' : ''
          } ${isRTL ? 'language-rtl' : 'language-english'}`}
          isHebrew={isRTL}
          config={config}
          showTocMenu={showTocMenu}
          showDownloadButton={
            /*
            for wix - only client preview
            for proposal - version history (live), editor preview, client preview
            */
            (isWix && showTocMenu && clientWixPreview) ||
            (!isWix &&
              ((isHistory && !isTemplate && backupPreviewId === 'live') ||
                ['editor-preview', 'client-preview'].includes(viewType)))
          }
        />
      )}

      <Layout.Content
        className={`publish-content ${fromActivityModal ? 'activity-modal-publish-content' : ''} ${
          screen === 'mobile' ? 'mobile-preview' : 'desktop-preview'
        } ${previewTemplate ? 'is-template-preview' : ''} ${
          isRTL ? 'language-rtl' : 'language-english'
        } ${!collapseContentTable && `content-wrapper-preview${!isRTL ? '-left' : ' '}`}`}>
        <>
          {isWixModalPreview ? null : <div className={`gap ${gap}`} />}

          {(prop?.approvedmanually || prop?.signature?.date) && !pathname.includes('/PDF/') && (
            <PublishApprovedInfo
              configText={configText}
              signature={prop?.signature}
              approvedManually={prop?.approvedmanually}
              language={prop?.language}
            />
          )}

          {publishSections}

          {fromActivityModal ||
          (sectionHeight &&
            !pathname.includes('/cd/') &&
            !clientWixPreview &&
            !pathname.includes('/PDF/') &&
            !previewTemplate &&
            !userWixPreview &&
            !/Mobi|Android/i.test(navigator.userAgent)) ? (
            <PageBreakers
              {...{
                sectionHeight,
                fromActivityModal,
                heatmaps,
                screen,
              }}
            />
          ) : (
            <div />
          )}

          {!isThumbnail && !!prop?.attachments?.length && (
            <AttachmentSection
              configText={configText}
              preview={true}
              setDownloadingFile={setDownloadingFile}
              {...props}
            />
          )}
          {pathname.includes('/cd/') && !prop?.approvedmanually && prop?.signature?.date ? (
            <ViralLoopItem
              text={'Securely signed with'}
              style={{
                bottom: '92px',
                position: 'fixed',
                ...(isRTL ? { left: '20px', right: 0 } : { right: '20px' }),
              }}
              isRtl={isRTL}
            />
          ) : null}
        </>
      </Layout.Content>

      {isHistory && (
        <AllVersionsSidebar
          proposal={prop}
          backupPreviewId={backupPreviewId}
          setBackupPreviewId={setBackupPreviewId}
          backupPreviewLoading={backupPreviewLoading}
          restoring={restoring}
        />
      )}

      {
        // for wix - only client preview
        // for prospero - only editor preview & client preview
        ((isWix && isWixClient) ||
          (!isWix && ['editor-preview', 'client-preview'].includes(viewType))) && (
          <PublishFooter
            setMakePayment={setMakePayment}
            configText={configText}
            name="signature"
            proposal={prop}
            publishedProposalLink={publishedProposalLink}
            isClient={!!((cid || ispdf || isWixClient) && !user)}
            templateUserWixPreview={templateUserWixPreview}
            spamUrls={spamUrls}
            variables={variables}
          />
        )
      }
    </Layout>
  );
};

PublishContent.defaultProps = {
  prop: '',
  config: null,
  previewTemplate: false,
  isPublishing: false,
  copyPropsalLink: () => {},
  publishedProposalLink: () => {},
  publishProposal: () => {},
  setMakePayment: () => {},
  downloadPdf: () => {},
  setProp: () => {},
  updateProposalClient: () => {},
  isTemplate: false,
  isSection: false,
  isHistory: false,
  wixPreview: false,
  clientWixPreview: false,
  templateWixPreview: false,
  isEditingModal: false,
  templateUserWixPreview: false,
  backupPreviewId: null,
  setBackupPreviewId: () => {},
  backupPreviewLoading: false,
};

PublishContent.propTypes = {
  prop: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  config: PropTypes.instanceOf(Object),
  previewTemplate: PropTypes.bool,
  publishProposal: PropTypes.func,
  isPublishing: PropTypes.bool,
  copyPropsalLink: PropTypes.func,
  publishedProposalLink: PropTypes.func,
  setMakePayment: PropTypes.func,
  downloadPdf: PropTypes.func,
  setProp: PropTypes.func,
  updateProposalClient: PropTypes.func,
  isTemplate: PropTypes.bool,
  isSection: PropTypes.bool,
  isHistory: PropTypes.bool,
  wixPreview: PropTypes.bool,
  clientWixPreview: PropTypes.bool,
  templateWixPreview: PropTypes.bool,
  userWixPreview: PropTypes.bool,
  isEditingModal: PropTypes.bool,
  templateUserWixPreview: PropTypes.bool,
  backupPreviewId: PropTypes.string,
  setBackupPreviewId: PropTypes.func,
  backupPreviewLoading: PropTypes.bool,
};

export default PublishContent;

const getSmallLargeFont = (fontSize) => {
  fontSize = parseInt(fontSize);

  if (!fontSize || isNaN(fontSize)) {
    return '';
  }

  if (fontSize <= 14) {
    return 'small-font';
  }
  if (fontSize >= 36) {
    return 'max-font';
  }
  if (fontSize >= 24) {
    return 'large-font';
  }

  return '';
};
